import React, { useEffect, useState } from "react";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { 
  getUserdata,
  editUserdata
} from "../../helper/generalHelper";
import { 
  FaPhone, 
  FaMapMarkerAlt, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaStar, 
  FaClock, 
  FaMoneyBillWave,
  FaGlobe,
  FaIdCard,
  FaCalendarAlt,
  FaBuilding,
  FaStore,
  FaCodeBranch
} from "react-icons/fa";
import { Link, useNavigate, useLocation } from "react-router-dom";
import TableSearch from "../../components/common/TableSearch";
import Pagination from "../../components/common/Pagination";
import { usePagination } from "../../hooks/usePagination";

const RestaurantList = ({ mode = "business" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.includes('/restaurant-staff') ? '/restaurant-staff/employee' : '/restaurant';

  const [restaurantList, setRestaurantList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("preferred_business_view") || "table");

  useEffect(() => {
    localStorage.setItem("preferred_business_view", viewMode);
  }, [viewMode]);

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    searchTerm,
    setSearchTerm,
    paginatedData,
    totalItems,
    totalPages,
  } = usePagination(restaurantList, ["business_name", "restaurant_code", "city", "state", "contact_number"]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const res = await api.get("restaurant/restaurant/all");
      let allRestaurants = res.data?.reataurants || [];
      
      // Filter based on mode (now strictly business or show all)
      const filtered = allRestaurants.filter(r => {
        return !r.parent_id || r.parent_id === "";
      });
      
      setRestaurantList(filtered);
    } catch (err) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (restaurant) => {
    setSelectedRestaurant(restaurant);
  };

  const handleCloseDetails = () => {
    setSelectedRestaurant(null);
  };

  const handleDeleteRestaurant = async (id) => {
    if (window.confirm(`Are you sure you want to delete this ${mode === 'business' ? 'business' : 'branch'}?`)) {
      // Optimistic update for real-time feel
      const prevList = [...restaurantList];
      setRestaurantList(restaurantList.filter(r => r.id !== id));
      
      try {
        await api.post(`restaurant/restaurant/delete`, { id });
        toast.success(`${mode === 'business' ? 'Business' : 'Branch'} deleted successfully`);
        fetchRestaurants();
      } catch (err) {
        setRestaurantList(prevList); // Revert on failure
        toast.error("Failed to delete");
      }
    }
  };

  const handleSwitchBusiness = async (restaurant) => {
    try {
      const res = await api.get(`restaurant/restaurant/switch?id=${restaurant.id}`);

      if (res.success) {
        editUserdata("restaurantId", restaurant.id);

        try {
          const currencyRes = await api.get("restaurant/currency/all");
          if (currencyRes.success && Array.isArray(currencyRes.data)) {
            const matchedCurrency = currencyRes.data.find(
              (c) => String(c.id) === String(restaurant.currency_id)
            );
            if (matchedCurrency) {
              localStorage.setItem("currency", matchedCurrency.code || "INR");
            } else {
              localStorage.setItem("currency", "INR");
            }
          }
        } catch (currencyErr) {
          console.error("Failed to load currencies:", currencyErr);
          localStorage.setItem("currency", "INR");
        }

        const dec = restaurant.decimal_places !== undefined && restaurant.decimal_places !== null 
          ? Number(restaurant.decimal_places) 
          : 2;
        const safeDec = [2, 3, 4].includes(dec) ? dec : 2;
        localStorage.setItem("currencyDecimals", safeDec);

        toast.success("Switched to business successfully");
        // Point to business page
        window.location.href = "/restaurant/business";
      } else {
        toast.error("Failed to switch business");
      }
    } catch (err) {
      console.error("Failed to switch business:", err);
      toast.error("An error occurred while switching");
    }
  };

  const getStatusBadge = (isActive) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isActive 
        ? "bg-green-100 text-green-800" 
        : "bg-red-100 text-red-800"
    }`}>
      <span className={`w-2 h-2 rounded-full mr-1 ${
        isActive ? "bg-green-500" : "bg-red-500"
      }`}></span>
      {isActive ? "Enabled" : "Disabled"}
    </span>
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    fetchRestaurants();
  }, [mode]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 pr-16 sm:p-6 sm:pr-20 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Business Management
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your primary restaurant brands
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-rose-50 rounded-lg text-rose-600 text-xl">
              {mode === "business" ? <FaStore /> : <FaCodeBranch />}
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Businesses</p>
              <p className="text-2xl font-bold text-gray-900">{restaurantList.length}</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-lg text-green-600 text-xl">
              <FaCalendarAlt />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {restaurantList.filter(r => r.is_active === 1).length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-4">
            <div className="p-3 bg-amber-50 rounded-lg text-amber-600 text-xl">
              <FaGlobe />
            </div>
            <div>
              <p className="text-sm text-gray-500">Regions</p>
              <p className="text-2xl font-bold text-gray-900">
                {[...new Set(restaurantList.map(r => r.city))].length}
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full mt-6">
          <TableSearch 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search businesses..." 
            className="flex-1 w-full"
          />
          <div className="flex gap-3 w-full sm:w-auto">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white flex-1 sm:flex-none"
            >
              {viewMode === 'grid' ? 'Table View' : 'Grid View'}
            </button>
            <Link 
              to={`${basePath}/addbusiness`} 
              className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700 shadow-md transition-all whitespace-nowrap flex-1 sm:flex-none text-center"
            >
              + Add Business
            </Link>
          </div>
        </div>
      </div>

      {restaurantList.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center">
          <div className="mx-auto w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-400 mb-4 text-2xl">
            {mode === "business" ? <FaStore /> : <FaCodeBranch />}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No businesses found</h3>
          <p className="text-gray-500 mt-1 mb-6">Get started by adding your first business.</p>
          <Link 
            to={`${basePath}/addbusiness`} 
            className="inline-flex items-center px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700"
          >
            + Add Business
          </Link>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedData.map((restaurant) => (
            <div 
              key={restaurant.id} 
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group"
            >
              {/* Card Header with Image */}
              <div className="relative h-40 bg-gray-100 overflow-hidden">
                <img 
                  src={restaurant.restaurant_images?.[0] || 'https://via.placeholder.com/400x200?text=No+Image'} 
                  alt={restaurant.business_name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4">
                  {restaurant.id === getUserdata("restaurantId") && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 shadow-sm border border-green-200">
                      <span className="w-2 h-2 rounded-full mr-1 bg-green-500"></span>
                      Active
                    </span>
                  )}
                </div>

              </div>

              <div className="p-5">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <Link to={`${basePath}/editbusiness?id=${restaurant.id}`} className="hover:text-rose-600 transition-colors">
                      <h3 className="text-lg font-bold text-gray-900 truncate" title={restaurant.business_name}>
                        {restaurant.business_name}
                      </h3>
                    </Link>
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                      <FaMapMarkerAlt className="text-rose-400" /> {restaurant.city}, {restaurant.state}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-gray-600">
                    <FaPhone className="w-4 mr-2 text-gray-400" /> {restaurant.contact_number}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <FaIdCard className="w-4 mr-2 text-gray-400" /> Code: {restaurant.restaurant_code}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                  <div className="flex gap-2 items-center">
                    {restaurant.id === getUserdata("restaurantId") ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2.5 py-1 rounded-full font-bold">
                        Active Business
                      </span>
                    ) : (
                      <button
                        onClick={() => handleSwitchBusiness(restaurant)}
                        className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-all"
                      >
                        Activate
                      </button>
                    )}
                    <button 
                      onClick={() => handleViewDetails(restaurant)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ml-1"
                      title="View Details"
                    >
                      <FaEye />
                    </button>

                  </div>
                  <button 
                    onClick={() => handleDeleteRestaurant(restaurant.id)}
                    className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden w-full">
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Business</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {paginatedData.map((restaurant) => (
                <tr 
                  key={restaurant.id} 
                  className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                  onClick={() => navigate(`${basePath}/editbusiness?id=${restaurant.id}`)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center group">
                      <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-gray-100 overflow-hidden mr-3">
                        <img 
                          src={restaurant.restaurant_images?.[0] || 'https://via.placeholder.com/40x40?text=R'} 
                          alt="" 
                          className="h-full w-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900 group-hover:text-rose-600 transition-colors">{restaurant.business_name}</div>
                        <div className="text-xs text-gray-500">Code: {restaurant.restaurant_code}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{restaurant.contact_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{restaurant.city}, {restaurant.state}</td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    {restaurant.id === getUserdata("restaurantId") ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2.5 py-1 rounded-full font-bold">
                        Active
                      </span>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleSwitchBusiness(restaurant); }}
                        className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded"
                      >
                        Activate
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-center gap-2 items-center">

                      <button 
                        onClick={(e) => { e.stopPropagation(); handleViewDetails(restaurant); }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <FaEye />
                      </button>

                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteRestaurant(restaurant.id); }}
                        className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {restaurantList.length > 0 && (
        <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      {/* Details Modal */}
      {selectedRestaurant && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden border border-gray-100">
            <div className="sticky top-0 bg-white px-8 py-6 border-b border-gray-100 flex justify-between items-center z-10 rounded-t-3xl">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">{selectedRestaurant.business_name}</h2>
                <p className="text-sm text-gray-500 mt-1">Business Information & Settings</p>
              </div>
              <button 
                onClick={handleCloseDetails}
                className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                {/* Left Column */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-5 pb-2 border-b border-gray-100">General Information</h3>
                  
                  <dl className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Business Name</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{selectedRestaurant.business_name}</dd>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Code</dt>
                      <dd className="text-sm text-gray-900 col-span-2 font-mono bg-gray-50 px-2 py-0.5 rounded w-max">{selectedRestaurant.restaurant_code}</dd>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Contact</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{selectedRestaurant.contact_number}</dd>
                    </div>
                    <div className="grid grid-cols-3 gap-4 pt-4 mt-4 border-t border-gray-50">
                      <dt className="text-sm font-medium text-gray-500">Location</dt>
                      <dd className="text-sm text-gray-900 col-span-2 leading-relaxed">
                        {selectedRestaurant.line1}{selectedRestaurant.line2 && `, ${selectedRestaurant.line2}`}<br />
                        {selectedRestaurant.city}, {selectedRestaurant.state}<br />
                        {selectedRestaurant.postal_code}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Right Column */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-5 pb-2 border-b border-gray-100">Configuration</h3>
                  
                  <dl className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Decimals</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{selectedRestaurant.decimal_places}</dd>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-gray-500">Created</dt>
                      <dd className="text-sm text-gray-900 col-span-2">{formatDate(selectedRestaurant.created_at)}</dd>
                    </div>
                  </dl>

                  <h3 className="text-lg font-semibold text-gray-900 mb-5 pb-2 border-b border-gray-100 mt-10">Media & Documents</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-3">Images</h4>
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {selectedRestaurant.restaurant_images?.map((img, idx) => (
                          <img key={idx} src={img} alt="" className="w-24 h-24 object-cover rounded-lg border border-gray-200 flex-shrink-0" />
                        )) || <span className="text-sm text-gray-400">No images provided</span>}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-3">Documents</h4>
                      <div className="space-y-2">
                        {selectedRestaurant.restaurant_documents?.map((doc, idx) => (
                          <a 
                            key={idx} 
                            href={doc.url} 
                            target="_blank" 
                            rel="noreferrer"
                            className="flex items-center justify-between p-3 rounded border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              <FaIdCard className="text-gray-400" />
                              <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{doc.name || `Document ${idx + 1}`}</span>
                            </div>
                            <span className="text-sm text-blue-600 hover:text-blue-800 font-medium">View</span>
                          </a>
                        )) || <span className="text-sm text-gray-400">No documents provided</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-10 pt-6 border-t border-gray-100 flex justify-end gap-3">
                <button 
                  onClick={handleCloseDetails}
                  className="px-6 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
                >
                  Close
                </button>
                <Link 
                  to={`${basePath}/editbusiness?id=${selectedRestaurant.id}`}
                  className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 transition-colors shadow-sm"
                >
                  Edit Information
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantList;
